import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";

// Fullscreen overlay covering the scanner controls once a capture finished:
// uploading spinner, processing progress pie, and the failed/error states
// with their retry/rescan actions. Renders nothing while idle (state null).
export const ScanStatusOverlay = ({
  state,
  error,
  progress,
  captureCount,
  onRetry,
  onRescan,
}) => {
  // Discard button
  const canDiscard = state === 'uploading' || state === 'processing';

  // Confirmation dialog state
  const [showingConfirmation, setShowingConfirmation] = useState(false);
  useEffect(
    () => {
      if (!canDiscard) { setShowingConfirmation(false); }
    },
    [canDiscard]
  );

  // The component
  if (!state) return null;
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 10,
        backgroundColor: "rgba(0,0,0,0.7)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        px: 4,
        textAlign: "center",
      }}
    >
      {state === "uploading" && (
        <>
          <CircularProgress sx={{ color: "#fff" }} />
          <Typography sx={{ color: "#fff" }}>
            Processing scan ({captureCount} images) …
          </Typography>
        </>
      )}

      {state === "processing" && (
        <>
          {/* progress pie chart */}
          <Box
            sx={(theme) => ({
              width: 128,
              height: 128,
              borderRadius: "50%",
              background: `conic-gradient(${theme.palette.primary.main} ${progress}%, rgba(255,255,255,0.25) ${progress}% 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            })}
          >
            <Typography variant="h5" sx={{ color: "#fff", fontWeight: "bold" }}>
              {Math.round(progress)}%
            </Typography>
          </Box>
        </>
      )}

      {state === "succeeded" && (
        <Typography variant="h6" sx={{ color: "#fff" }}>
          Scan processed successfully.
        </Typography>
      )}

      {state === "failed" && (
        <>
          <Alert
            severity="error"
            sx={{ width: "100%", maxWidth: 420, textAlign: "left" }}
          >
            {error}
          </Alert>
          <Button variant="contained" onClick={onRescan}>
            Scan again
          </Button>
        </>
      )}

      {state === "error" && (
        <>
          <Alert
            severity="error"
            sx={{ width: "100%", maxWidth: 420, textAlign: "left" }}
          >
            {error}
          </Alert>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button variant="contained" onClick={onRetry}>
              Retry
            </Button>
            <Button
              variant="outlined"
              onClick={onRescan}
              sx={{ color: "#fff", borderColor: "#fff" }}
            >
              Scan again
            </Button>
          </Box>
        </>
      )}

      {
        canDiscard && (
          <>
            {/* Discard button */}
            <Button
              variant='outlined'
              onClick={() => setShowingConfirmation(true)}
              sx={{ 
                color: '#fff', 
                borderColor: '#fff',
                textTransform: 'none',
                px: 4,
                py: 1.2
              }}
            >
              Discard
            </Button>

            {/* Discard confirmation */}
            <Dialog
              open={showingConfirmation}
              onClose={() => setShowingConfirmation(false)}
              sx={{ zIndex: 1500 }}
            >
              <DialogTitle>
                Discard the scan?
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  The current scan is still processing
                  and will be disregarded if you continue.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowingConfirmation(false)}>
                  Cancel
                </Button>
                <Button
                  color='error'
                  onClick=
                    {
                      () => {
                        setShowingConfirmation(false);
                        onRescan();
                      }
                    }
                >
                  Discard
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )
      }
    </Box>
  );
};

ScanStatusOverlay.propTypes = {
  state: PropTypes.oneOf([
    "uploading",
    "processing",
    "succeeded",
    "failed",
    "error",
  ]),
  error: PropTypes.string,
  progress: PropTypes.number.isRequired,
  captureCount: PropTypes.number.isRequired,
  onRetry: PropTypes.func.isRequired,
  onRescan: PropTypes.func.isRequired,
};
